import { IStorage, ILogger } from '~/packages/core';
import {
    ITaskPersistenceManager,
    TaskMetadata,
    AITaskStorageData
} from '../types/internal-types';

// Use a single storage key for all tasks
const TASKS_STORAGE_KEY = 'ai_tasks_state';

export class TaskPersistenceManager implements ITaskPersistenceManager {
    private storage: IStorage;
    private logger: ILogger;

    constructor(storage: IStorage, logger: ILogger) {
        this.storage = storage;
        this.logger = logger;
    }

    async persistTasks(tasksMetadata: TaskMetadata[]): Promise<void> {
        try {
            const storageData: AITaskStorageData = {
                tasks: {},
                timestamp: new Date().toISOString()
            };

            // Convert TaskMetadata array to storage format
            for (const taskMetadata of tasksMetadata) {
                const { id, ...metadata } = taskMetadata;
                storageData.tasks[id] = metadata;
            }

            await this.storage.setObject(TASKS_STORAGE_KEY, storageData);
            this.logger.debug('Persisted task state to storage', {
                taskCount: tasksMetadata.length
            });
        } catch (error) {
            this.logger.error('Failed to persist task state:', error);
            // Don't throw - app exit should not be blocked by storage failures
        }
    }

    async getStoredTasks(): Promise<TaskMetadata[]> {
        try {
            const storedState = await this.storage.getObject<AITaskStorageData>(
                TASKS_STORAGE_KEY
            );

            if (!storedState || !storedState.tasks) {
                return [];
            }

            // Convert storage format to TaskMetadata array
            const tasksMetadata: TaskMetadata[] = [];
            for (const [taskId, metadata] of Object.entries(
                storedState.tasks
            )) {
                tasksMetadata.push({
                    id: taskId,
                    ...metadata
                });
            }

            return tasksMetadata;
        } catch (error) {
            this.logger.error('Failed to get stored tasks:', error);
            return [];
        }
    }

    async cleanup(): Promise<void> {
        try {
            await this.storage.remove(TASKS_STORAGE_KEY);
            this.logger.debug('Cleaned up task storage to prevent stale state');
        } catch (error) {
            this.logger.warn('Failed to clean up task storage:', error);
        }
    }
}
